/**
 * Build a campaign in four steps: details, content, audience, review.
 *
 * A campaign is the one record here with several unrelated decisions in it —
 * who it goes to, what it says, and on which channel — and a single long form
 * made it easy to save one that could never reach anybody. The steps exist to
 * put the reach figure and the warnings in front of someone before they save,
 * not to decorate the form.
 *
 * Formatting is channel-aware rather than universal, because the channels are
 * not alike: WhatsApp renders its own *bold* markers, email can carry HTML only
 * down the CSV-export path (a mailto: hand-off is text, so the send console
 * flattens it), and SMS carries no formatting at all.
 */
import { useState, useMemo, useEffect } from 'react';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { CHANNELS, GOALS, channelLabel, channelIcon } from '../../constants/marketing.js';
import { parseDate, formatDate, pad2 } from '../../lib/dates.js';
import { audienceOf, reachSummary, applyMerge, campaignRecipients } from '../../domain/marketing.js';
import Sheet, { Field } from '../Sheet.jsx';
import MailPreview from '../MailPreview.jsx';
import { useInlineImages } from '../../lib/inlineImages.js';
import CampaignEditor, { looksHtml } from './CampaignEditor.jsx';
import CampaignAudience from './CampaignAudience.jsx';
import WhatsAppStep, { fillTemplate } from './WhatsAppStep.jsx';
import PushStep from './PushStep.jsx';

const STEPS = ['Details', 'Content', 'Audience', 'Review'];

/*
 * Draft and Scheduled are stored. "Send now" is not a status — nothing in the
 * workspace can send on its own — so it saves as a draft and opens the send
 * console, which is the only thing "now" can honestly mean here.
 */
const PLANS = [
  ['Draft', 'Save it and come back'],
  ['Scheduled', 'Goes out on its own, at the time you pick'],
  ['now', 'Save and open the send console']
];

/**
 * Everything worth warning about before a campaign is saved.
 *
 * A WhatsApp campaign sending an approved template is a different thing: its
 * words were written and approved in WhatsApp Manager, so nothing typed here
 * reaches anybody and none of the copy checks apply.
 */
function reviewWarnings(form, viaTemplate) {
  const body = String(form.body || '');
  const out = [];

  if (viaTemplate) {
    /*
     * A WhatsApp campaign is an approved template with its blanks filled.
     * Nothing else about the copy can be wrong, but those two things can.
     */
    if (!String(form.waTemplate || '').trim()) {
      out.push({ level: 'stop', text: 'Choose the approved template this goes out as.' });
    } else {
      const blanks = (String(form.waBody || '').match(/\{\{\d+\}\}/g) || []).length;
      const filled = (form.waFields || []).filter(v => String(v || '').trim()).length;
      if (filled < blanks) {
        out.push({ level: 'stop', text: `Fill every blank in the template — ${blanks - filled} still empty.` });
      }
      const top = form.waHeader && form.waHeader.format;
      if (top && top !== 'text' && !String(form.waHeaderUrl || '').trim()) {
        out.push({ level: 'stop', text: `This template has ${top === 'image' ? 'an image' : `a ${top}`} on top — add one.` });
      }
    }
    return out;
  }

  if (form.channel === 'email' && !String(form.subject || '').trim()) {
    out.push({ level: 'stop', text: 'An email needs a subject line.' });
  }
  if (!body.trim()) {
    out.push({ level: 'stop', text: 'There is no message yet.' });
  } else if (body.trim().length < 40) {
    out.push({ level: 'warn', text: 'The message is very short — say why you are writing.' });
  }
  if (body && !body.includes('{{')) {
    out.push({ level: 'warn', text: 'Nothing is personalised. Add a merge field such as {{first}}.' });
  }
  if (body && !/https?:\/\/|\{\{link\}\}/i.test(body)) {
    out.push({ level: 'warn', text: 'No link to act on — add one so the message has a next step.' });
  }
  if (form.channel === 'sms' && body.length > 160) {
    out.push({ level: 'warn', text: `SMS is cut at 160 characters; this is ${body.length}.` });
  }
  return out;
}

export default function CampaignSheet({ id, preset, onClose }) {
  const {
    S, mkt, mktCtx, saveCampaign, saveTemplate, queueSend, unqueue, sendPushNow, schedulePush, setSheet, toast
  } = useApp();

  const existing = id ? mkt.campaigns.find(c => c.id === id) : null;
  const [form, setForm] = useState(() => existing || {
    name: '', channel: preset || 'email', audience: '', contacts: [], status: 'Draft',
    // The template named in channel setup is only the starting point.
    waTemplate: (S.mkt || {}).waTemplate || '',
    waTemplateLang: (S.mkt || {}).waTemplateLang || '',
    schedule: '', goal: '', subject: '', body: '', accountId: ''
  });

  /*
   * The mailboxes this campaign could go out as. Fetched here rather than read
   * from settings, because the browser is never told a mail password and the
   * list a campaign author needs is only the names and the addresses.
   */
  const [senders, setSenders] = useState([]);

  useEffect(() => {
    if (isOffline()) return;
    api.marketing.senders()
      .then(out => setSenders(out.senders || []))
      .catch(() => { /* the picker just stays on Auto */ });
  }, []);
  const [plan, setPlan] = useState(() => (existing ? existing.status : 'Draft'));
  const [step, setStep] = useState(1);
  const [html, setHtml] = useState(() => looksHtml(existing && existing.body));

  /* Why Next refused, shown on the step itself. */
  const [refused, setRefused] = useState('');

  const set = (k, v) => { setRefused(''); setForm(f => ({ ...f, [k]: v })); };

  /* Who this reaches, recomputed as the channel, segment or contact list moves. */
  const { audience, all, ok, notes } = useMemo(() => {
    const found = mkt.audiences.find(a => a.id === form.audience) || null;
    const everyone = campaignRecipients(
      { audience: form.audience, contacts: form.contacts }, mktCtx);
    const summary = reachSummary(everyone, form.channel, mktCtx);
    return { audience: found, all: everyone, ok: summary.ok, notes: summary.notes };
  }, [form.audience, form.contacts, form.channel, mkt.audiences, mktCtx]);

  /*
   * WhatsApp cannot carry free text to somebody who has not messaged in the
   * last 24 hours, so a campaign goes out as an approved template. When one is
   * chosen, the template is the message and this step only shows it.
   */
  /*
   * A WhatsApp campaign is always a template: a business may send free text
   * only inside the 24 hours after somebody last messaged it, and a campaign
   * is business-initiated by definition. Which template is the campaign's
   * own choice; the one in channel setup is only the default for a new one.
   */
  const isPush = form.channel === 'push';
  const pushTopic = (S.mkt || {}).pushTopic || '';
  const viaTemplate = form.channel === 'whatsapp';
  const waTemplate = form.waTemplate || '';

  /*
   * A campaign started from a channel's own screen belongs to that channel.
   * Editing an existing one is the same: changing the channel underneath copy
   * already written for it is not an edit, it is a different campaign.
   */
  const fixedChannel = !!preset || !!id;

  const warnings = reviewWarnings(form, viaTemplate);
  const blocking = warnings.filter(w => w.level === 'stop');

  /*
   * The audience is chosen after the copy is written, so preferring it would
   * leave this step with nothing to preview against on every new campaign.
   * Falling back to any client in the book keeps the merge fields resolving
   * against a real record from the first keystroke.
   */
  const sample = useMemo(
    () => ok[0] || all[0] || audienceOf({ src: 'clients' }, mktCtx)[0] || null,
    [ok, all, mktCtx]
  );

  /*
   * The picker speaks ISO; the record keeps the workspace's own date format,
   * with the time on it — "due today" is not much use for a send meant to go
   * out at nine in the morning.
   */
  const scheduleISO = (() => {
    const d = parseDate(form.schedule);
    if (!d) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
      + `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  })();

  const setSchedule = iso => {
    if (!iso) { set('schedule', ''); return; }
    const [datePart, timePart] = String(iso).split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    // A date picked with the time left blank means the start of the working
    // day, not midnight — nobody schedules a campaign for 00:00.
    const [hh, mm] = (timePart || '09:00').split(':').map(Number);
    set('schedule', formatDate(new Date(y, m - 1, d, hh || 0, mm || 0), true, S.dateFmt));
  };

  const merged = useInlineImages(sample ? applyMerge(form.body, sample) : '');

  /*
   * Saved wording for this channel.
   *
   * Templates existed with nowhere to be used: a campaign could not reach one,
   * so every saved message was written out again by hand and the whole screen
   * was decoration. Only this channel's are offered - an SMS template inside
   * an email campaign is 160 characters of plain text where a subject line and
   * markup are wanted.
   */
  const reusable = mkt.templates.filter(t => t.channel === form.channel);

  /*
   * Keep this wording.
   *
   * The other half of a library, and the half that makes it fill up: a
   * template written from nothing is a chore, while one saved from a campaign
   * that has just been written and is about to go out costs a name and a
   * click.
   */
  const keepAsTemplate = () => {
    if (!String(form.body || '').trim()) { toast('Write the message first.'); return; }

    // eslint-disable-next-line no-alert
    const name = window.prompt('Keep this wording as a template called:',
      String(form.name || '').trim() || 'Untitled');
    if (!name || !name.trim()) return;

    saveTemplate(null, {
      name: name.trim(),
      channel: form.channel,
      subject: form.subject || '',
      body: form.body || ''
    });
    toast('Kept \u2014 it is in Marketing \u2192 Templates');
  };

  const useTemplate = templateId => {
    const t = reusable.find(x => x.id === templateId);
    if (!t) return;

    /* Asked, not assumed: overwriting something already written is the one
       thing on this screen a keystroke cannot undo. */
    // eslint-disable-next-line no-alert
    if (String(form.body || '').trim() && !window.confirm('Replace what is written with this template?')) {
      return;
    }

    setForm(f => ({
      ...f,
      body: t.body || '',
      subject: f.channel === 'email' ? (t.subject || f.subject) : f.subject
    }));
    setHtml(looksHtml(t.body || ''));
    toast(`Loaded ${t.name}`);
  };
  const richPreview = form.channel === 'email' && looksHtml(merged);

  const previewBody = () => (richPreview
    ? (
      <MailPreview html={merged} />
    )
    : <div className="cpbody">{merged || 'Nothing written yet.'}</div>);

  /*
   * Step gates. Each one names the single thing still missing.
   *
   * A refused Next also *shows* why, on the step, rather than only raising a
   * toast: a toast at the top of the screen while you are looking at an empty
   * subject box reads as nothing happening, and the button looks broken.
   */
  const stop = why => { setRefused(why); toast(why); };

  const next = () => {
    setRefused('');

    if (step === 1 && !String(form.name).trim()) { stop('Name the campaign first.'); return; }

    if (step === 2 && isPush && !String(form.pushTitle || '').trim() && !String(form.pushBody || '').trim()) {
      stop('A push needs a title or a message.'); return;
    }
    if (step === 2 && isPush) {
      const pmode = form.pushMode || 'app';
      if (pmode === 'test') {
        const tokenList = String(form.pushTokens || '').split('\n').map(t => t.trim()).filter(Boolean);
        if (!tokenList.length) { stop('Add at least one device token to test to.'); return; }
      } else if (pmode === 'topic') {
        if (!String(form.pushTopic || pushTopic).trim()) {
          stop('Name the topic to send to, or set a default in Channel setup.'); return;
        }
      }
    }

    if (step === 2 && !isPush && blocking.length) { stop(blocking[0].text); return; }

    if (step === 3 && !isPush && !form.audience && !(form.contacts || []).length) {
      stop('Choose at least one contact to send to.');
      return;
    }

    setStep(s => Math.min(4, s + 1));
  };

  const save = async () => {
    const status = plan === 'now' ? 'Draft' : plan;
    const next = {
      ...form,
      status,
      name: String(form.name).trim(),
      schedule: status === 'Scheduled' ? form.schedule : ''
    };

    const written = saveCampaign(id, next);
    const campaignId = id || written;

    /*
     * Push is not a per-recipient send: it goes to a topic Firebase fans out.
     * So it does not open the send console - "now" sends straight away, and a
     * schedule enqueues the one notification.
     */
    if (isPush) {
      const pmode = form.pushMode || 'app';
      const tokens = pmode === 'test'
        ? String(form.pushTokens || '').split('\n').map(t => t.trim()).filter(Boolean)
        : [];
      const note = {
        id: campaignId,
        pushTitle: form.pushTitle,
        pushBody: form.pushBody,
        pushImage: form.pushImage,
        pushLink: form.pushLink,
        pushMode: pmode,
        pushSource: pmode === 'app' ? 'app' : '',
        pushApp: pmode === 'app' ? (form.pushApp || 'all') : '',
        pushTopic: pmode === 'topic' ? (form.pushTopic || pushTopic) : '',
        pushTokens: tokens
      };
      try {
        if (plan === 'now') {
          await sendPushNow(note);
        } else if (status === 'Scheduled' && next.schedule) {
          await schedulePush(note);
        } else if (id) {
          unqueue(campaignId);
        }
      } catch (e) {
        toast(e.message);
        return;
      }
      onClose();
      return;
    }

    /*
     * A scheduled campaign is worked out now and sent later.
     *
     * Who it reaches, what each person's copy says, which addresses have opted
     * out — all of that is decided here, because all of it is read from things
     * that live in this browser. The server is handed finished messages and a
     * time, and at that time it sends them. The audience is therefore fixed at
     * this moment rather than at the hour it goes out, which is the honest
     * reading of "send this to these people on Friday".
     */
    if (status === 'Scheduled' && next.schedule) {
      try {
        await queueSend({ ...next, id: campaignId });
      } catch (e) {
        // Saved either way: the campaign exists and its row will say it is
        // overdue, which is better than losing the work to a failed request.
        toast(`Saved, but not scheduled — ${e.message}`);
      }
    } else if (id) {
      // No longer scheduled, so nothing should be waiting to go out.
      unqueue(campaignId);
    }

    onClose();
    if (plan === 'now') setSheet({ kind: 'send', id: campaignId });
  };

  return (
    <Sheet
      icon="mega"
      tone="g-pink"
      title={id ? 'Edit campaign' : 'New campaign'}
      /* The widest sheet there is. Writing a message in a 720px column meant a
         cramped source box above a preview too narrow to tell you anything
         about how the email will actually land. */
      wide
      onClose={onClose}
      onSave={step === 4 ? save : next}
      saveLabel={step === 4 ? (plan === 'now' ? 'Save and open console' : 'Save campaign') : 'Next'}
      footerLeft={step > 1
        ? <button type="button" className="btn" onClick={() => setStep(s => s - 1)}>Back</button>
        : null}
    >
      <Field label="" wide>
        <ol className="wsteps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            return (
              <li key={label} className={n === step ? 'on' : n < step ? 'done' : ''}>
                <span className="wnum">{n < step ? <Icon name="check" /> : n}</span>
                <span>{label}</span>
              </li>
            );
          })}
        </ol>
      </Field>

      {refused ? (
        <Field label="" wide>
          <p className="cstop"><Icon name="alert" /><span>{refused}</span></p>
        </Field>
      ) : null}

      {step === 1 ? (
        <>
          <Field
            label="Channel"
            wide
            hint={fixedChannel ? (
              <div className="fhint">
                {id
                  ? 'The channel is settled once a campaign exists — the copy, the limits and the '
                    + 'addresses all belong to it. Write a new one for another channel.'
                  : `Started from the ${channelLabel(form.channel)} screen. Use All campaigns to `
                    + 'write for another channel.'}
              </div>
            ) : undefined}
          >
            <div className="chips">
              {/*
                * Arriving from the Email screen means an email campaign. Showing
                * WhatsApp, SMS and Push as choices there invites a mistake that
                * is only noticed after the wrong thing has been written — the
                * subject line disappears, the copy limits change, and the
                * audience is measured against a different address.
                *
                * The one it is goes on showing, greyed, rather than the row
                * vanishing: what channel this is remains worth stating.
                */}
              {(fixedChannel
                ? CHANNELS.filter(([value]) => value === form.channel)
                : CHANNELS
              ).map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={`chip${form.channel === value ? ' on' : ''}`}
                  disabled={fixedChannel}
                  onClick={() => set('channel', value)}
                >
                  <Icon name={channelIcon(value)} />{label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Campaign name" wide>
            <input
              autoFocus
              value={form.name || ''}
              placeholder="Trial ending — convert"
              onChange={e => set('name', e.target.value)}
            />
          </Field>

          {form.channel === 'email' ? (
            <Field
              label="Send from"
              wide
              hint={
                <div className="fhint">
                  {senders.length
                    ? 'The chosen mailbox goes first; the rest stay behind it if it refuses.'
                    : 'No mail account is switched on — add one in Channel setup.'}
                </div>
              }
            >
              <select value={form.accountId || ''} onChange={e => set('accountId', e.target.value)}>
                <option value="">Whichever is first in Channel setup</option>
                {senders.map(x => (
                  <option key={x.id} value={x.id}>
                    {x.name}{x.from ? ` — ${x.from}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <Field label="Goal" wide>
            <div className="chips">
              {GOALS.map(([label, icon]) => (
                <button
                  type="button"
                  key={label}
                  className={`chip${form.goal === label ? ' on' : ''}`}
                  onClick={() => set('goal', form.goal === label ? '' : label)}
                >
                  <Icon name={icon} />{label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="When" wide>
            <div className="plans">
              {PLANS.map(([value, blurb]) => (
                <button
                  type="button"
                  key={value}
                  className={`planopt${plan === value ? ' on' : ''}`}
                  onClick={() => setPlan(value)}
                >
                  <b>{value === 'now' ? 'Send now' : value}</b>
                  <small>{blurb}</small>
                </button>
              ))}
            </div>
          </Field>

          {plan === 'Scheduled' ? (
            <Field
              label="Goes out at"
              wide
              hint={
                <div className="fhint">
                  The server sends it at that time whether anybody is signed in or not.
                  Everyone it reaches, and what each of them receives, is worked out when you
                  save &mdash; so a contact added afterwards is not included, and editing the
                  campaign schedules it again with whoever it reaches then.
                </div>
              }
            >
              <input
                type="datetime-local"
                value={scheduleISO}
                onChange={e => setSchedule(e.target.value)}
              />
            </Field>
          ) : null}
        </>
      ) : null}

      {step === 2 && isPush ? (
        <PushStep form={form} set={set} sample={sample} defaultTopic={pushTopic} />
      ) : null}

      {step === 2 && !isPush && viaTemplate ? (
        <WhatsAppStep form={form} set={set} sample={sample} S={S} />
      ) : null}

      {step === 2 && !viaTemplate && !isPush ? (
        <>
          {form.channel === 'email' ? (
            <Field
              label="Subject"
              wide
              hint={
                <div className="fhint">
                  {String(form.subject || '').length}/60 characters — shorter subjects survive a
                  phone screen.
                </div>
              }
            >
              <input
                value={form.subject || ''}
                placeholder="Your trial ends on Friday"
                onChange={e => set('subject', e.target.value)}
              />
            </Field>
          ) : null}

          <Field
            label="Template"
            wide
            hint={
              <div className="fhint">
                {reusable.length
                  ? 'Loading one copies the wording in so you can change it here; the template '
                    + 'itself is untouched. Keeping one saves what is written now, under a name, '
                    + 'for the next campaign to start from.'
                  : 'Nothing saved yet. Write the message below, then keep it: the next campaign '
                    + 'can start from it instead of from nothing.'}
              </div>
            }
          >
            <div className="tplrow">
              {/*
                * Shown even when the list is empty, greyed, rather than hidden.
                * Hiding it made the screen say that starting from a template
                * was not possible here, when in fact there simply were none
                * yet - and somebody who has never saved one is exactly the
                * person who needs to be told that they can.
                */}
              <select
                value=""
                disabled={!reusable.length}
                onChange={e => useTemplate(e.target.value)}
              >
                <option value="">
                  {reusable.length ? 'Start from a template' : 'No templates saved yet'}
                </option>
                {reusable.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>

              <button type="button" className="btn sm" onClick={keepAsTemplate}>
                <Icon name="file" />Keep as a template
              </button>

              <button
                type="button"
                className="btn sm"
                onClick={() => setSheet({ kind: 'template', preset: form.channel })}
              >
                <Icon name="plus" />Write one
              </button>
            </div>
          </Field>

          <Field label="Message" wide>
            <CampaignEditor
              channel={form.channel}
              html={html}
              onHtml={setHtml}
              body={form.body}
              onBody={v => set('body', v)}
              signature={S.signature || S.senderName || ''}
            />
          </Field>

          <Field label="Live preview" wide>
            {sample ? (
              <div className="cpreview">
                {form.channel === 'email' && form.subject
                  ? <div className="cpsub">{applyMerge(form.subject, sample)}</div> : null}
                {previewBody()}
              </div>
            ) : (
              <p className="muted">There are no clients in the book yet to preview against.</p>
            )}
          </Field>

          {warnings.length ? (
            <Field label="" wide>
              <ul className="cwarn">
                {warnings.map(w => (
                  <li key={w.text} className={w.level}>
                    <Icon name={w.level === 'stop' ? 'x' : 'alert'} />{w.text}
                  </li>
                ))}
              </ul>
            </Field>
          ) : null}
        </>
      ) : null}

      {step === 3 && isPush ? (
        <Field label="Who gets it" wide>
          <div className="cpreview">
            <div className="cpbody">
              {(form.pushMode || 'app') === 'test' ? (
                <>
                  A test send — this goes only to the{' '}
                  <b>{String(form.pushTokens || '').split('\n').filter(x => x.trim()).length} device(s)</b>{' '}
                  you listed, and to no one else. Live users are not touched. Switch to App users in
                  the message step when you are ready to send for real.
                </>
              ) : (form.pushMode || 'app') === 'topic' ? (
                <>
                  A push goes to every device subscribed to the topic
                  <b> {form.pushTopic || pushTopic || '—'}</b> — there is no contact list to pick
                  from here. Firebase decides who that is; the app subscribes them.
                </>
              ) : (
                <>
                  Goes to everyone who installed and logged into{' '}
                  <b>{(form.pushApp && form.pushApp !== 'all') ? `the ${form.pushApp} app` : 'a XYRA app'}</b>.
                  The list is read live from the app when this goes out — no contact list to pick here.
                </>
              )}
            </div>
          </div>
        </Field>
      ) : null}

      {step === 3 && !isPush ? (
        <Field label="" wide>
          <CampaignAudience
            channel={form.channel}
            audienceId={form.audience}
            contacts={form.contacts || []}
            onContacts={v => set('contacts', v)}
          />
        </Field>
      ) : null}

      {step === 4 ? (
        <>
          <Field label="Campaign summary" wide>
            <div className="rtable">
              <div><span>Channel</span><b><Icon name={channelIcon(form.channel)} />{channelLabel(form.channel)}</b></div>
              <div><span>Name</span><b>{form.name || '—'}</b></div>
              <div><span>Goal</span><b>{form.goal || '—'}</b></div>
              {viaTemplate ? <div><span>Template</span><b>{waTemplate}</b></div> : null}
              {isPush && (form.pushMode || 'app') === 'test' ? (
                <div><span>Test to</span><b>{String(form.pushTokens || '').split('\n').filter(x => x.trim()).length} device(s)</b></div>
              ) : isPush && (form.pushMode || 'app') === 'topic' ? (
                <div><span>Topic</span><b>{form.pushTopic || pushTopic || '—'}</b></div>
              ) : isPush ? (
                <div><span>App users</span><b>{(form.pushApp && form.pushApp !== 'all') ? form.pushApp : 'All apps'}</b></div>
              ) : null}
              {isPush ? <div><span>Title</span><b>{form.pushTitle || '—'}</b></div> : null}
              {form.channel === 'email' ? (
                <div>
                  <span>Sends from</span>
                  <b>
                    {(senders.find(x => x.id === form.accountId) || {}).from
                      || (senders[0] || {}).from
                      || 'the first mail account'}
                  </b>
                </div>
              ) : null}
              <div>
                <span>Status</span>
                <b>
                  {plan === 'now' ? 'Opening the send console' : plan}
                  {plan === 'Scheduled' && form.schedule ? ` · ${form.schedule}` : ''}
                </b>
              </div>
              <div>
                <span>Audience</span>
                <b>{(form.contacts || []).length} individual contacts</b>
              </div>
              <div>
                <span>Reach</span>
                <b>
                  {ok.length} reachable
                  {notes.length ? ` · skipped: ${notes.join(', ')}` : ''}
                </b>
              </div>
            </div>
          </Field>

          <Field label="Message preview" wide>
            {viaTemplate ? (
              <div className="mprev">
                <div className="mphead">Approved template · {waTemplate || 'none chosen'}</div>
                <div className="mpbody">
                  <div className="cpbody">
                    {sample
                      ? applyMerge(fillTemplate(form.waBody, form.waFields), sample)
                      : fillTemplate(form.waBody, form.waFields) || 'Nothing chosen.'}
                  </div>
                </div>
              </div>
            ) : sample ? (
              <div className="mprev">
                {form.channel === 'email' ? (
                  <div className="mphead">Subject: {applyMerge(form.subject, sample) || '—'}</div>
                ) : (
                  <div className="mphead">{channelLabel(form.channel)}</div>
                )}
                <div className="mpbody">{previewBody()}</div>
              </div>
            ) : <p className="muted">Nothing to preview.</p>}
          </Field>

          {warnings.length ? (
            <Field label="" wide>
              <ul className="cwarn">
                {warnings.map(w => (
                  <li key={w.text} className={w.level}>
                    <Icon name={w.level === 'stop' ? 'x' : 'alert'} />{w.text}
                  </li>
                ))}
              </ul>
            </Field>
          ) : (
            <Field label="" wide>
              <p className="muted">Nothing outstanding — this one is ready.</p>
            </Field>
          )}
        </>
      ) : null}
    </Sheet>
  );
}
